import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { search } from '../../actions/search';
import SearchBar from '../SearchBar';
import Link from '../Link';
import styles from './ContractTypeAhead.style';
import AutosuggestContractItem from '../AutosuggestContractItem';
import Callout from '../Callout';

const ContractTypeAhead = ({
  field: { name },
  form: { errors = {}, setFieldValue = () => {}, setFieldTouched = () => {}, values = {} },
  label,
  required,
  callout,
  className,
  handleCallback,
  contractName,
  rootStyle,
  ...other
}) => {
  const error = get(errors, name);
  let errorMessage;
  let accentError = false;
  if (error) {
    accentError = true;
    errorMessage = error.contractId;
  }

  const dispatch = useDispatch();
  const [input, setInput] = useState(contractName);
  const [isLoading, setIsLoading] = useState(true);
  const [showContractSuggestions, setShowContractSuggestions] = useState(true);

  const suggestions = useSelector(state => get(state, 'search.results'));
  const contractSuggestions = get(suggestions, 'contracts', [])
    .map(contract => ({
      approvalStatus: contract.approvalStatus,
      value: contract.contractId,
      label: contract.name,
      secondaryLabel: contract.contractId,
      contract,
    }))
    .slice(0, 5);

  const setFormValue = value => {
    setFieldValue(name, value);
    setFieldTouched(name);
  };

  const handleChange = async newInput => {
    setInput(newInput);
    setFormValue('');
    if (!newInput) return;
    setIsLoading(true);
    setShowContractSuggestions(true);
    await dispatch(search(['contracts'], newInput));
    setIsLoading(false);
  };

  // eslint-disable-next-line no-shadow
  const onContractItemClick = ({ label, contract }) => {
    setInput(label);
    setFormValue(contract);
    setShowContractSuggestions(false);
    handleCallback(contract, errors, setFieldValue, setFieldTouched, values);
  };

  const handleClear = () => {
    setInput('');
    setFormValue('');
  };

  const getSuggestions = () => {
    if (!showContractSuggestions) {
      return null;
    }
    return contractSuggestions;
  };

  const Section = ({ list, loading }) => (
    <Stack>
      {list.length && !loading ? (
        <Stack>
          {list.map(props => AutosuggestContractItem({ ...props, onClick: onContractItemClick }))}
        </Stack>
      ) : (
        <Stack>
          {loading ? (
            <Spinner size={SpinnerSize.small} className={styles.loadingSpinner} />
          ) : (
            <Stack>
              <Text block className={styles.title}>
                No results
              </Text>
              <Link className={styles.moreResults} href="/contracts" target="_blank">
                View all <strong>Contracts</strong>
              </Link>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );

  Section.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    loading: PropTypes.bool.isRequired,
  };

  const Dropdown = () => (
    <Stack className={styles.root}>
      <Section list={contractSuggestions} loading={isLoading} />
    </Stack>
  );

  return (
    <Stack style={rootStyle}>
      {label && (
        <Stack horizontal verticalAlign="start">
          <Label>{label}</Label>
          {required && <Text className={styles.asterisk}>*</Text>}
          {callout && <Callout content={callout} />}
        </Stack>
      )}
      <SearchBar
        placeholder="Search Term"
        onChange={handleChange}
        onRenderDropdown={Dropdown}
        isLoading={isLoading}
        className={className}
        suggestions={getSuggestions()}
        value={input}
        onClear={handleClear}
        underlined={false}
        accentError={accentError}
        ariaLabel={label}
        aria-required={required}
        {...other}
      />

      {errorMessage && (
        <div role="alert">
          <p className={styles.errorMessage}>
            <span data-testid="contract-typeahead-required" data-automation-id="error-message">
              {errorMessage}
            </span>
          </p>
        </div>
      )}
    </Stack>
  );
};

ContractTypeAhead.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}),
    values: PropTypes.shape({}),
    setFieldTouched: PropTypes.func,
    setFieldValue: () => {},
  }).isRequired,
  className: PropTypes.string,
  label: PropTypes.string,
  callout: PropTypes.string,
  contractName: PropTypes.string,
  required: PropTypes.bool,
  handleCallback: () => {},
  rootStyle: PropTypes.shape({}),
};

ContractTypeAhead.defaultProps = {
  className: '',
  required: false,
  label: null,
  callout: null,
  contractName: '',
  handleCallback: null,
  rootStyle: null,
};

export default ContractTypeAhead;
